import React, { useState, useEffect, useRef, useCallback, useMemo, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Card, Button, Badge, LoadingSpinner } from '../components/SharedComponents';
import { API_BASE } from '../config';
import { SYNTHESIS_STATUS, normalizeSynthesisStatus, SYNTHESIS_COPY } from '../utils/synthesisStatus';
import './InterviewWizard.css';

/* ─── SVG Icons ────────────────────────────────────────────────── */
const Icons = {
  category: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  confirm: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  interview: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  mic: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ),
  micOff: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .67-.1 1.32-.27 1.93" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ),
  stop: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" />
    </svg>
  ),
  arrowLeft: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  arrowRight: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  material: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    </svg>
  ),
  supplier: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20h20" />
      <path d="M5 20V8l5 4V8l5 4V4h3a2 2 0 0 1 2 2v14" />
    </svg>
  ),
  retry: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  complete: (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

const STEPS = [
  { id: 1, label: 'Your Role', icon: Icons.category },
  { id: 2, label: 'Your Focus', icon: Icons.confirm },
  { id: 3, label: 'Interview', icon: Icons.interview },
];

const FIELD_LABELS = {
  strategic_notes: 'Strategic notes',
  market_notes: 'Market notes',
  supplier_notes: 'Supplier notes',
  relationship_notes: 'Relationship notes',
  risk_notes: 'Risk notes',
  procurement_priorities: 'Procurement priorities',
  business_context: 'Business context',
};

// Phase labels for progress indicator (fallback if backend doesn't return them)
const PHASE_LABELS_DEFAULT = {
  phase0: 'Context Setting',
  phase1: 'Relationship & Supplier Understanding',
  phase2: 'Targeted Gaps',
  phase3: 'Materials & Categories',
  phase4: 'Operational Reality',
  phase5: 'Connections & Dependencies',
  phase6: 'Synthesis & Confirmation',
  phase7: 'Open Floor',
};

/* ─── Error Boundary ───────────────────────────────────────────── */
class InterviewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="interview-page">
          <div className="interview-error-boundary">
            {Icons.warning}
            <h2 className="interview-error-boundary-title">Something went wrong</h2>
            <p className="interview-error-boundary-text">
              The interview page encountered an error. Please try refreshing.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              icon={Icons.retry}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ─── Progress Indicator ───────────────────────────────────────── */
function StepIndicator({ currentStep, completedSteps, steps }) {
  const stepList = steps || STEPS;
  return (
    <div className="interview-steps">
      {stepList.map((step, idx) => {
        const isActive = step.id === currentStep;
        const isComplete = (completedSteps || []).includes(step.id);
        const isPast = step.id < currentStep;

        return (
          <React.Fragment key={step.id}>
            {idx > 0 && (
              <div className={`interview-step-connector ${isPast || isActive ? 'interview-step-connector--active' : ''}`} />
            )}
            <div className={`interview-step ${isActive ? 'interview-step--active' : ''} ${isComplete || isPast ? 'interview-step--complete' : ''}`}>
              <div className="interview-step-circle">
                {isComplete || isPast ? Icons.check : <span>{step.id}</span>}
              </div>
              <span className="interview-step-label">{step.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─── Error Banner ─────────────────────────────────────────────── */
function ErrorBanner({ message, onRetry }) {
  return (
    <div className="interview-error">
      <span>{message}</span>
      {onRetry && (
        <button className="interview-error-retry" onClick={onRetry}>
          {Icons.retry}
          <span>Retry</span>
        </button>
      )}
    </div>
  );
}

/* ─── API Helper (stable — reads token from ref) ───────────────── */
async function apiFetchDirect(url, tokenValue, options = {}) {
  const headers = {
    'Authorization': `Bearer ${tokenValue}`,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // For FormData, remove Content-Type so browser sets boundary
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data.detail) detail = data.detail;
    } catch {
      // Response wasn't JSON — use status-based message
    }
    throw new Error(detail);
  }

  return res.json();
}

/* ─── Voice Input Component ────────────────────────────────────── */
function VoiceInput({
  voiceSupported,
  isRecording,
  isTranscribing,
  recordingQuestionIdx,
  questionIdx,
  onStartRecording,
  onStopRecording,
  disabled,
}) {
  const isThisRecording = isRecording && recordingQuestionIdx === questionIdx;
  const isThisTranscribing = isTranscribing && recordingQuestionIdx === questionIdx;

  if (!voiceSupported) {
    return (
      <span className="interview-voice-unsupported">
        {Icons.micOff}
        Voice input not supported in this browser
      </span>
    );
  }

  if (isThisTranscribing) {
    return (
      <button
        className="interview-voice-btn interview-voice-btn--transcribing"
        disabled
        title="Transcribing audio..."
      >
        <svg className="interview-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
        <span>Transcribing...</span>
      </button>
    );
  }

  return (
    <button
      className={`interview-voice-btn ${isThisRecording ? 'interview-voice-btn--recording' : ''}`}
      onClick={() => isThisRecording ? onStopRecording() : onStartRecording(questionIdx)}
      disabled={disabled || (isRecording && !isThisRecording)}
      title={isThisRecording ? 'Stop recording' : 'Record voice answer'}
    >
      {isThisRecording ? Icons.stop : Icons.mic}
      <span>{isThisRecording ? 'Stop' : 'Record'}</span>
    </button>
  );
}

/* ─── Main Wizard Component ────────────────────────────────────── */
function InterviewWizardInner({ portalMode, portalToken, onComplete, onAuthExpired }) {
  const navigate = useNavigate();
  const { token: authToken } = useAuth();

  // Portal mode: use portal token; normal mode: use auth token
  const effectiveToken = portalMode ? portalToken : authToken;

  // API path prefix: portal mode uses /api/interview/portal, normal uses /api/interview/v2
  const v2Prefix = portalMode ? '/api/interview/portal' : '/api/interview/v2';
  const answerPath = portalMode ? '/api/interview/portal/session/answer' : '/api/interview/v2/session/answer';
  const transcribePath = portalMode ? '/api/interview/portal/transcribe' : '/api/interview/v2/transcribe';
  const confirmPath = portalMode ? '/api/interview/portal/session/confirm' : '/api/interview/v2/session/confirm';

  // Keep token in a ref so async functions always read the latest value
  const tokenRef = useRef(effectiveToken);
  useEffect(() => { tokenRef.current = effectiveToken; }, [effectiveToken]);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Session state (shared across rounds)
  const [sessionId, setSessionId] = useState(null);
  const [roundNumber, setRoundNumber] = useState(1);
  const [questions, setQuestions] = useState([]);

  const [synthesisSubmitting, setSynthesisSubmitting] = useState(false);
  const [synthesisSubmitError, setSynthesisSubmitError] = useState(null);

  // Follow-up rounds (2 & 3) state — one question at a time
  const [followUpIdx, setFollowUpIdx] = useState(0);
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
  const [followUpError, setFollowUpError] = useState(null);

  // Review & Confirm state
  const [fieldWrites, setFieldWrites] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [gapsClosed, setGapsClosed] = useState([]);

  // Done state
  const [interviewComplete, setInterviewComplete] = useState(false);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingQuestionIdx, setRecordingQuestionIdx] = useState(null);
  const [transcribeMessage, setTranscribeMessage] = useState(null);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [barHeights, setBarHeights] = useState(() => new Array(40).fill(0));
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const animFrameRef = useRef(null);

  // ── Interview State ───────────────────────────────────────
  const [v2Roles, setV2Roles] = useState([]);
  const [v2SelectedRole, setV2SelectedRole] = useState(null);
  const [v2EntitySteps, setV2EntitySteps] = useState([]);     // steps array from backend
  const [v2CurrentStep, setV2CurrentStep] = useState(0);      // current entity selection step
  const [v2StepSelections, setV2StepSelections] = useState({}); // {categories: [...], suppliers: [...], accounts: [...]}
  const [v2StepItems, setV2StepItems] = useState([]);          // items for current paginated step
  const [v2StepSearch, setV2StepSearch] = useState('');         // search query for current step
  const [v2StepPage, setV2StepPage] = useState(1);             // pagination page
  const [v2StepTotalCount, setV2StepTotalCount] = useState(0); // total items for pagination
  const [v2HasCategoryFilter, setV2HasCategoryFilter] = useState(false); // whether supplier list has category pre-filter
  const [v2SkipSelection, setV2SkipSelection] = useState(false); // leadership skip
  const [v2WorksBroadly, setV2WorksBroadly] = useState({ categories: false, suppliers: false, accounts: false });
  const [v2RoleDescription, setV2RoleDescription] = useState(''); // free-text for "other" role
  const [v2EntityConfig, setV2EntityConfig] = useState(null); // backward compat for skip check
  const [v2RolesLoading, setV2RolesLoading] = useState(false);
  const [v2EntitiesLoading, setV2EntitiesLoading] = useState(false);
  const [v2StartLoading, setV2StartLoading] = useState(false);
  const [v2Error, setV2Error] = useState(null);
  const [v2Phase, setV2Phase] = useState(null);
  // Item 1.3b: track synthesis_status so the Phase 6 review can distinguish
  // "synthesis failed" from "synthesis succeeded but produced no summary."
  // NULL on legacy rows normalizes to COMPLETE — see utils/synthesisStatus.js.
  const [synthesisStatusRaw, setSynthesisStatusRaw] = useState(null);
  const [v2WelcomeMessage, setV2WelcomeMessage] = useState('');
  const [v2QuestionGenLoading, setV2QuestionGenLoading] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [v2PhaseSummaries, setV2PhaseSummaries] = useState({});
  const [v2PhaseLabels, setV2PhaseLabels] = useState({});
  const [v2ConfirmSynthLoading, setV2ConfirmSynthLoading] = useState(false);

  // Demo mode: auto-generate answers for QA testing (?demo=true)
  const isDemoMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('demo') === 'true';
  }, []);
  const [demoFillLoading, setDemoFillLoading] = useState(false);
  const [demoFillAllLoading, setDemoFillAllLoading] = useState(false);
  const [demoFillAllProgress, setDemoFillAllProgress] = useState('');

  // Mount tracking
  const mountedRef = useRef(true);
  // Tracks whether onComplete has been called (M1 fix — prevent repeated calls)
  const completionCalledRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (analyserRef.current) analyserRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Stable fetch wrapper (with portal 401 interception) ──── */
  const apiFetch = useCallback(async (url, options) => {
    try {
      return await apiFetchDirect(url, tokenRef.current, options);
    } catch (err) {
      // Portal mode: expired token → return user to passcode screen
      if (portalMode && onAuthExpired && err.message && err.message.includes('(401)')) {
        onAuthExpired();
        const authErr = new Error('AUTH_EXPIRED');
        authErr.isAuthExpired = true;
        throw authErr;
      }
      throw err;
    }
  }, [portalMode, onAuthExpired]);


  // ── Mount: Load Roles + Check Existing Session ──────────
  useEffect(() => {
    if (!effectiveToken) return;
    const initV2 = async () => {
      setV2RolesLoading(true);
      try {
        // Load roles
        const rolesData = await apiFetchDirect(`${v2Prefix}/roles`, tokenRef.current);
        if (!mountedRef.current) return;
        setV2Roles(rolesData?.roles || []);

        // Check for existing session (resume)
        const sessData = await apiFetchDirect(`${v2Prefix}/session`, tokenRef.current);
        if (!mountedRef.current) return;
        const session = sessData?.session;
        if (session && session.status === 'in_progress') {
          setSessionId(session.id);
          setV2Phase(session.phase);
          setSynthesisStatusRaw(session.synthesis_status || null);
          setV2SelectedRole(session.role);
          // Restore selections from session focus_areas (typed dict format)
          const fa = session.focus_areas || { categories: [], suppliers: [], accounts: [] };
          setV2StepSelections(typeof fa === 'object' && !Array.isArray(fa) ? fa : { categories: [], suppliers: [], accounts: [] });
          const rn = session.round_number || 1;
          setRoundNumber(rn);
          setQuestions(sessData?.questions || []);
          if (session.phase_summaries) setV2PhaseSummaries(session.phase_summaries);
          if (session.phase_labels) setV2PhaseLabels(session.phase_labels);

          // Determine which step we're on based on phase
          if (session.phase === 'profiled' || (session.phase && session.phase.includes('phase0'))) {
            setCompletedSteps([1, 2]);
            setCurrentStep(3);
          } else if (session.phase === 'review') {
            setCompletedSteps([1, 2]);
            setCurrentStep(3);
            if (session.pending_field_writes && Array.isArray(session.pending_field_writes)) {
              setFieldWrites(session.pending_field_writes.map(fw => ({ ...fw })));
            }
          } else if (session.phase === 'complete') {
            setInterviewComplete(true);
            setCompletedSteps([1, 2, 3]);
            setCurrentStep(3);
          } else {
            // Any other active phase
            setCompletedSteps([1, 2]);
            setCurrentStep(3);
          }

          // One-shot: check if synthesis completed while user was away
          if (session.synthesis_status === 'pending') {
            try {
              const statusData = await apiFetchDirect(`${v2Prefix}/session/status?session_id=${session.id}`, tokenRef.current);
              if (!mountedRef.current) return;
              if (statusData?.pending_field_writes?.length > 0) {
                setFieldWrites(statusData.pending_field_writes.map(fw => ({ ...fw })));
                setV2Phase('review');
              }
            } catch {
              // Resume continues with whatever state we have
            }
          }
        }
      } catch (err) {
        if (mountedRef.current) setV2Error('Could not load interview data. Please try again.');
      } finally {
        if (mountedRef.current) setV2RolesLoading(false);
      }
    };
    initV2();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveToken]);

  // Check voice support
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && !navigator.mediaDevices?.getUserMedia) {
        setVoiceSupported(false);
      }
    } catch {
      setVoiceSupported(false);
    }
  }, []);

  /* ─── Start Over ─────────────────────────────────────────── */
  const startOver = async () => {
    abandonV2Session();
  };

  /* ─── v2: Abandon session and start fresh ─────────────────── */
  const abandonV2Session = useCallback(async () => {
    if (!sessionId) return;
    try {
      await apiFetch(`${v2Prefix}/session/${sessionId}/abandon`, { method: 'POST' });
    } catch {
      // If abandon fails, still reset locally
    }
    completionCalledRef.current = false;
    setCurrentStep(1);
    setCompletedSteps([]);
    setSessionId(null);
    setRoundNumber(1);
    setQuestions([]);
    setFollowUpIdx(0);
    setFollowUpAnswer('');
    setFollowUpSubmitting(false);
    setFollowUpError(null);
    setFieldWrites([]);
    setInterviewComplete(false);
    setV2Phase(null);
    setSynthesisStatusRaw(null);
    setV2SelectedRole(null);
    setV2StepSelections({ categories: [], suppliers: [], accounts: [] });
    setV2EntitySteps([]);
    setV2CurrentStep(0);
    setV2StepItems([]);
    setV2StepSearch('');
    setV2StepPage(1);
    setV2StepTotalCount(0);
    setV2HasCategoryFilter(false);
    setV2SkipSelection(false);
    setV2WelcomeMessage('');
    setV2Error(null);
    setV2QuestionGenLoading(false);

    setV2PhaseSummaries({});
    setV2PhaseLabels({});
    setV2ConfirmSynthLoading(false);
    setGapsClosed([]);
    setSynthesisSubmitting(false);
    setIsRecording(false);
    setIsTranscribing(false);
    setRecordingQuestionIdx(null);
    setTranscribeMessage(null);
    setBarHeights(new Array(40).fill(0));
  }, [apiFetch, sessionId]);

  /* ─��─ Follow-up Rounds: Answer Submit ──────────────────────── */
  const submitFollowUpAnswer = async (isLast) => {
    setFollowUpSubmitting(true);
    setFollowUpError(null);
    try {
      await apiFetch(answerPath, {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          question_index: followUpIdx,
          answer_text: followUpAnswer,
        }),
      });

      if (!mountedRef.current) return;

      if (isLast) {
        handleV2SubmitAnswers();
      } else {
        // Advance to next question
        setFollowUpIdx(prev => prev + 1);
        setFollowUpAnswer('');
        setTranscribeMessage(null);
      }
    } catch (err) {
      if (mountedRef.current) {
        setFollowUpError(err.message || 'Could not save your answer. Please try again.');
      }
    } finally {
      if (mountedRef.current) setFollowUpSubmitting(false);
    }
  };

  /* ─── Skip/Don't Know: Submit question as skipped ─────────────── */
  const skipCurrentQuestion = async () => {
    setFollowUpSubmitting(true);
    setFollowUpError(null);
    try {
      await apiFetch(answerPath, {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          question_index: followUpIdx,
          skipped: true,
        }),
      });
      if (!mountedRef.current) return;

      const isLast = followUpIdx === questions.length - 1;
      if (isLast) {
        handleV2SubmitAnswers();
      } else {
        setFollowUpIdx(prev => prev + 1);
        setFollowUpAnswer('');
        setTranscribeMessage(null);
      }
    } catch (err) {
      if (mountedRef.current) {
        setFollowUpError(err.message || 'Could not skip question.');
      }
    } finally {
      if (mountedRef.current) setFollowUpSubmitting(false);
    }
  };

  /* ─── Review: Confirm Field Writes ──────────────────────────── */
  const confirmFieldWrites = async () => {
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      await apiFetch(confirmPath, {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          field_writes: fieldWrites,
        }),
      });

      if (!mountedRef.current) return;
      // /confirm already marks session complete — no need to call /complete
      setInterviewComplete(true);
    } catch (err) {
      if (mountedRef.current) {
        setConfirmError(err.message || 'Could not save your knowledge. Please try again.');
      }
    } finally {
      if (mountedRef.current) setConfirmLoading(false);
    }
  };

  /* ─── Voice Recording ──────────────────────────────────────── */
  const startWaveformLoop = (analyser) => {
    const barCount = 40;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(dataArray);
      const len = dataArray.length;
      const heights = [];
      for (let i = 0; i < barCount; i++) {
        const idx = Math.floor((i / barCount) * len * 0.8);
        heights.push(Math.min(112, ((dataArray[idx] / 255) * 1.5) * 112));
      }
      setBarHeights(heights);
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  };

  const stopWaveform = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setBarHeights(new Array(40).fill(0));
  };

  const startRecording = async (questionIdx) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingQuestionIdx(questionIdx);

      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        audioCtxRef.current = audioCtx;
        analyserRef.current = analyser;
        startWaveformLoop(analyser);
      } catch {
        // Web Audio not supported — recording still works, just no visualizer
      }

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob, questionIdx);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      setVoiceSupported(false);
    }
  };

  const stopRecording = () => {
    stopWaveform();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob, questionIdx) => {
    setIsTranscribing(true);
    setTranscribeMessage(null);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const res = await fetch(`${API_BASE}${transcribePath}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tokenRef.current}` },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.transcript || data.text) {
          const text = data.transcript || data.text;

          setFollowUpAnswer(prev => prev ? `${prev} ${text}` : text);
          setTranscribeMessage({ type: 'success', text: "Transcribed — don't worry about spelling or grammar, only edit if the meaning is wrong" });
        } else {
          setTranscribeMessage({ type: 'error', text: 'Voice transcription unavailable — please type your answer' });
        }
      } else {
        setTranscribeMessage({ type: 'error', text: 'Voice transcription unavailable — please type your answer' });
      }
    } catch {
      setTranscribeMessage({ type: 'error', text: 'Voice transcription unavailable — please type your answer' });
    } finally {
      if (mountedRef.current) setIsTranscribing(false);
    }
  };


  /* ─── Demo Mode: Auto-fill answer ─────────────────────────────── */
  const demoAutoFill = useCallback(async (questionText) => {
    if (!sessionId || !questionText) return '';
    // Collect prior Q&A from answered questions in this session
    const priorQa = questions
      .filter(q => q.answer_text && q.id !== questions[followUpIdx]?.id)
      .map(q => ({ question: q.question_text, answer: q.answer_text }));

    const data = await apiFetch('/api/interview/v2/demo/generate-answer', {
      method: 'POST',
      body: JSON.stringify({
        question_text: questionText,
        company_id: null, // backend uses JWT company_id
        role: v2SelectedRole,
        focus_areas: v2StepSelections,
        phase: roundNumber - 1, // round 1 = phase 0, etc.
        prior_qa: priorQa,
      }),
    });
    return data?.answer || '';
  }, [apiFetch, sessionId, questions, followUpIdx, v2SelectedRole, v2StepSelections, roundNumber]);

  const handleDemoAutoFill = useCallback(async () => {
    const question = questions[followUpIdx];
    if (!question) return;
    setDemoFillLoading(true);
    try {
      const answer = await demoAutoFill(question.question_text);
      if (mountedRef.current && answer) {
        setFollowUpAnswer(answer);
      }
    } catch (err) {
      console.error('Demo auto-fill failed:', err);
    } finally {
      if (mountedRef.current) setDemoFillLoading(false);
    }
  }, [demoAutoFill, questions, followUpIdx]);

  const handleDemoAutoFillAll = useCallback(async () => {
    if (!questions.length) return;
    setDemoFillAllLoading(true);
    // Find the index of unanswered questions starting from current
    const startIdx = followUpIdx;
    const total = questions.length - startIdx;
    for (let i = startIdx; i < questions.length; i++) {
      if (!mountedRef.current) break;
      setDemoFillAllProgress(`Filling ${i - startIdx + 1} of ${total}...`);
      try {
        const answer = await demoAutoFill(questions[i].question_text);
        if (!mountedRef.current) break;
        if (answer) {
          // Save via the same endpoint the normal flow uses
          await apiFetch(answerPath, {
            method: 'POST',
            body: JSON.stringify({
              session_id: sessionId,
              question_index: i,
              answer_text: answer,
            }),
          });
          // Update local state so subsequent demo calls have prior Q&A
          setQuestions(prev => prev.map((q, idx) =>
            idx === i ? { ...q, answer_text: answer } : q
          ));
          // If this is the currently displayed question, fill the textarea
          if (i === followUpIdx) {
            setFollowUpAnswer(answer);
          }
        }
      } catch (err) {
        console.error(`Demo auto-fill failed for question ${i + 1}:`, err);
      }
    }
    if (mountedRef.current) {
      setDemoFillAllLoading(false);
      setDemoFillAllProgress('');
      // Advance to the last question so user can hit Submit
      if (questions.length > 0) {
        setFollowUpIdx(questions.length - 1);
        // Set the answer for the last question in the textarea
        const lastQ = questions[questions.length - 1];
        if (lastQ?.answer_text) {
          setFollowUpAnswer(lastQ.answer_text);
        }
      }
    }
  }, [apiFetch, demoAutoFill, questions, followUpIdx, sessionId]);

  /* ─── Render: Follow-up Round (shared for v1 Rounds 2-3 and all v2 phases) ─── */
  const renderFollowUpRound = () => {
    const totalQuestions = questions.length;
    const question = questions[followUpIdx];
    const isLast = followUpIdx === totalQuestions - 1;

    const phaseNum = roundNumber || 1;
    const roundIndicatorText = `Phase ${phaseNum} — Question ${followUpIdx + 1} of ${totalQuestions}`;

    if (!question) {
      return (
        <div className="interview-empty-state">
          {Icons.warning}
          <p className="interview-empty-state-text">
            No follow-up questions were generated. Please try refreshing.
          </p>
        </div>
      );
    }

    return (
      <div className="interview-followup">
        <div className="interview-round-header">
          <div className="interview-round-indicator">{roundIndicatorText}</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isDemoMode && (
              <button
                className="interview-demo-btn"
                onClick={handleDemoAutoFillAll}
                disabled={demoFillAllLoading}
              >
                {demoFillAllLoading ? demoFillAllProgress || 'Filling...' : 'Auto-fill All'}
              </button>
            )}
          </div>
        </div>


        <Card className="interview-question-card interview-followup-question">
          <h3 className="interview-question-text">
            {question.question_text || 'Question text unavailable'}
          </h3>

          <div className="interview-answer-area">
            <div className="interview-answer-swap">
              <div className={`interview-answer-layer ${isRecording ? 'interview-answer-layer--hidden' : ''}`}>
                <textarea
                  className="interview-textarea"
                  value={followUpAnswer}
                  onChange={(e) => {
                    setFollowUpAnswer(e.target.value);
                    setTranscribeMessage(null);
                  }}
                  placeholder={isTranscribing ? 'Transcribing...' : 'Type your answer here...'}
                  rows={5}
                  disabled={followUpSubmitting || isTranscribing}
                />
              </div>
              <div className={`interview-waveform-layer ${isRecording ? 'interview-waveform-layer--visible' : ''}`}>
                <div className="interview-waveform">
                  {barHeights.map((h, i) => (
                    <div
                      key={i}
                      className="interview-waveform-bar"
                      style={{ height: `${Math.max(4, h)}px` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {transcribeMessage && (
              <div className={`interview-transcribe-message interview-transcribe-message--${transcribeMessage.type}`}>
                {transcribeMessage.text}
              </div>
            )}

            <div className="interview-answer-controls">
              <VoiceInput
                voiceSupported={voiceSupported}
                isRecording={isRecording}
                isTranscribing={isTranscribing}
                recordingQuestionIdx={recordingQuestionIdx}
                questionIdx={followUpIdx}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                disabled={followUpSubmitting}
              />
              {isDemoMode && (
                <button
                  className="interview-demo-btn"
                  onClick={handleDemoAutoFill}
                  disabled={demoFillLoading || followUpSubmitting}
                  style={{ marginLeft: '8px' }}
                >
                  {demoFillLoading ? 'Generating...' : 'Auto-fill'}
                </button>
              )}
            </div>
          </div>

          {followUpError && (
            <div className="interview-answer-error">{followUpError}</div>
          )}
        </Card>

        <div className="interview-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            onClick={() => submitFollowUpAnswer(isLast)}
            disabled={followUpSubmitting || !followUpAnswer.trim()}
            icon={followUpSubmitting ? null : Icons.arrowRight}
          >
            {followUpSubmitting ? 'Saving...' : isLast ? 'Submit' : 'Next'}
          </Button>
        </div>
      </div>
    );
  };

  /* ─── Render: Review & Confirm ──────────────────────────────── */
  const renderReview = () => {
    // Group field writes by entity
    const groups = {};
    fieldWrites.forEach((fw, idx) => {
      const key = `${fw.entity_type}-${fw.entity_id}`;
      if (!groups[key]) {
        groups[key] = {
          entityType: fw.entity_type,
          entityId: fw.entity_id,
          entityName: fw.entity_name || fw.entity_id,
          fields: [],
        };
      }
      groups[key].fields.push({ ...fw, _idx: idx });
    });

    return (
      <div className="interview-review">
        <div className="interview-step-header">
          <h2 className="interview-step-title">Review your answers before saving</h2>
          <p className="interview-step-subtitle">
            These are the knowledge entries extracted from your interview. Edit any values before confirming.
          </p>
        </div>

        {Object.entries(groups).map(([key, group]) => (
          <div key={key} className="interview-review-group">
            <div className="interview-review-group-header">
              <span className="interview-review-group-type">{group.entityType}</span>
              {group.entityName}
            </div>
            {group.fields.map((field) => (
              <div key={field._idx} className="interview-review-field">
                <div className="interview-review-field-label">
                  {FIELD_LABELS[field.field_name] || field.field_name}
                </div>
                <textarea
                  className="interview-review-textarea"
                  value={field.merged_value || ''}
                  onChange={(e) => {
                    setFieldWrites(prev => {
                      const updated = [...prev];
                      updated[field._idx] = { ...updated[field._idx], merged_value: e.target.value };
                      return updated;
                    });
                  }}
                  rows={3}
                  disabled={confirmLoading}
                />
              </div>
            ))}
          </div>
        ))}

        {/* Intelligence Gaps Closed */}
        {gapsClosed && gapsClosed.length > 0 && (
          <div style={{
            marginTop: '8px', marginBottom: '16px', padding: '16px 20px',
            backgroundColor: '#F0FDF4', borderRadius: '10px', border: '1px solid #BBF7D0',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#166534', marginBottom: '10px' }}>
              This interview closed {gapsClosed.length} intelligence gap{gapsClosed.length !== 1 ? 's' : ''}:
            </div>
            {gapsClosed.map((g, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', color: '#15803D' }}>
                <span style={{ fontWeight: 500 }}>{g.entity_name}</span>
                <span style={{ color: '#6B7280' }}>{(g.gap_category || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                <span style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                  backgroundColor: g.priority === 'critical' ? '#FEE2E2' : g.priority === 'high' ? '#FEF3C7' : '#DBEAFE',
                  color: g.priority === 'critical' ? '#DC2626' : g.priority === 'high' ? '#D97706' : '#2563EB',
                }}>{g.priority}</span>
              </div>
            ))}
          </div>
        )}

        {confirmError && <ErrorBanner message={confirmError} />}

        <div className="interview-actions">
          <Button
            variant="primary"
            size="large"
            onClick={confirmFieldWrites}
            disabled={confirmLoading}
            icon={confirmLoading ? null : Icons.check}
          >
            {confirmLoading ? 'Saving...' : 'Confirm and save'}
          </Button>
        </div>
      </div>
    );
  };

  /* ─── Render: Done Screen ───────────────────────────────────── */
  const renderDone = () => {
    // Portal mode: call onComplete callback exactly once
    if (portalMode && onComplete && !completionCalledRef.current) {
      completionCalledRef.current = true;
      setTimeout(() => onComplete(), 100);
    }
    return (
      <div className="interview-completion">
        <div className="interview-completion-icon">{Icons.complete}</div>
        <h2 className="interview-completion-title">Thank you</h2>
        <p className="interview-completion-text">
          Your knowledge has been added to Wingman. The team's procurement intelligence is now stronger because of your input.
        </p>
        {!portalMode && (
          <Button variant="primary" size="large" onClick={() => navigate('/')}>
            Return to dashboard
          </Button>
        )}
      </div>
    );
  };

  /* ─── v2: Load Entities When Role Changes ───────────────────── */
  const loadV2Entities = useCallback(async (role) => {
    setV2EntitiesLoading(true);
    setV2Error(null);
    setV2EntitySteps([]);
    setV2StepSelections({ categories: [], suppliers: [], accounts: [] });
    setV2CurrentStep(0);
    setV2SkipSelection(false);
    setV2WorksBroadly({ categories: false, suppliers: false, accounts: false });
    setV2EntityConfig(null);
    try {
      const data = await apiFetch(`${v2Prefix}/entities?role=${encodeURIComponent(role)}`);
      if (!mountedRef.current) return;

      // Leadership skips entity selection entirely
      if (data.skip_selection) {
        setV2SkipSelection(true);
        setV2EntityConfig({ skip_selection: true });
        return;
      }

      setV2EntitySteps(data.steps || []);
      setV2EntityConfig({ skip_selection: false });
    } catch (err) {
      if (mountedRef.current) setV2Error('Could not load entities.');
    } finally {
      if (mountedRef.current) setV2EntitiesLoading(false);
    }
  }, [apiFetch]);

  /* ─── v2: Handle Role Selection ──────────────────────────────── */
  const handleV2RoleSelect = useCallback((roleId) => {
    setV2SelectedRole(roleId);
    setV2Error(null);
    if (roleId !== 'other') setV2RoleDescription('');
    loadV2Entities(roleId);
  }, [loadV2Entities]);

  /* ─── v2: Handle Entity Toggle (multi-step) ──────────────────── */
  const handleV2EntityToggle = useCallback((entityType, entity) => {
    const key = entityType === 'category' ? 'categories' : entityType === 'account' ? 'accounts' : 'suppliers';
    const step = v2EntitySteps[v2CurrentStep];
    const maxSel = step?.max_select || 10;

    setV2StepSelections(prev => {
      const current = prev[key] || [];
      const exists = current.find(e =>
        (e.id && e.id === entity.id) || (e.name && e.name === entity.name)
      );
      if (exists) {
        return { ...prev, [key]: current.filter(e =>
          !((e.id && e.id === entity.id) || (e.name && e.name === entity.name))
        )};
      }
      if (current.length >= maxSel) return prev;
      return { ...prev, [key]: [...current, { id: entity.id || null, name: entity.name }] };
    });
  }, [v2EntitySteps, v2CurrentStep]);

  /* ─── v2: Load paginated items for current step ─────────────── */
  const loadStepItems = useCallback(async (stepIdx, search = '', page = 1) => {
    const step = v2EntitySteps[stepIdx];
    if (!step) return;
    const etype = step.entity_type;

    // Categories come inline in the step config
    if (etype === 'category') {
      setV2StepItems(step.items || []);
      setV2StepTotalCount((step.items || []).length);
      return;
    }

    setV2EntitiesLoading(true);
    try {
      let url;
      if (etype === 'supplier') {
        const cats = (v2StepSelections.categories || []).map(c => c.name).join(',');
        url = `${v2Prefix}/suppliers?page=${page}&page_size=20`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (cats && step.pre_filter_by === 'category') url += `&category_names=${encodeURIComponent(cats)}`;
      } else if (etype === 'account') {
        url = `${v2Prefix}/accounts?page=${page}&page_size=20`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
      }
      if (url) {
        const data = await apiFetch(url);
        if (!mountedRef.current) return;
        const items = data.suppliers || data.accounts || [];
        setV2StepItems(items);
        setV2StepTotalCount(data.total_count || items.length);
        setV2HasCategoryFilter(!!data.has_category_filter);
      }
    } catch (err) {
      if (mountedRef.current) setV2Error('Could not load items.');
    } finally {
      if (mountedRef.current) setV2EntitiesLoading(false);
    }
  }, [v2EntitySteps, v2StepSelections.categories, apiFetch]);

  /* ─── v2: Search debounce for entity lists ──────────────────── */
  const searchTimeoutRef = useRef(null);
  const handleStepSearch = useCallback((value) => {
    setV2StepSearch(value);
    setV2StepPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadStepItems(v2CurrentStep, value, 1);
    }, 300);
  }, [v2CurrentStep, loadStepItems]);

  /* ─── v2: Auto-load items when entity step changes ───────────── */
  useEffect(() => {
    if (currentStep !== 2 || v2EntitySteps.length === 0 || v2CurrentStep >= v2EntitySteps.length) return;
    const step = v2EntitySteps[v2CurrentStep];
    if (!step) return;

    setV2StepSearch('');
    setV2StepPage(1);

    if (step.entity_type === 'category') {
      setV2StepItems(step.items || []);
      setV2StepTotalCount((step.items || []).length);
      setV2HasCategoryFilter(false);
      return;
    }

    // Paginated step — fetch directly (not via useCallback to avoid stale closures)
    let cancelled = false;
    (async () => {
      setV2EntitiesLoading(true);
      try {
        let url;
        if (step.entity_type === 'supplier') {
          const cats = (v2StepSelections.categories || []).map(c => c.name).join(',');
          url = `${v2Prefix}/suppliers?page=1&page_size=20`;
          if (cats && step.pre_filter_by === 'category') url += `&category_names=${encodeURIComponent(cats)}`;
        } else if (step.entity_type === 'account') {
          url = `${v2Prefix}/accounts?page=1&page_size=20`;
        }
        if (url) {
          const data = await apiFetch(url);
          if (cancelled || !mountedRef.current) return;
          const items = data.suppliers || data.accounts || [];
          setV2StepItems(items);
          setV2StepTotalCount(data.total_count || items.length);
          setV2HasCategoryFilter(!!data.has_category_filter);
        }
      } catch (err) {
        if (!cancelled && mountedRef.current) setV2Error('Could not load items.');
      } finally {
        if (!cancelled && mountedRef.current) setV2EntitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2CurrentStep, currentStep, v2EntitySteps]);

  /* ─── v2: Start Session ──────────────────────────────────────── */
  const handleV2StartSession = useCallback(async () => {
    const isLeadership = v2SkipSelection;
    if (!v2SelectedRole) return;
    setV2StartLoading(true);
    setV2Error(null);
    try {
      const focusAreas = isLeadership
        ? { categories: [], suppliers: [], accounts: [] }
        : {
            ...v2StepSelections,
            ...(v2WorksBroadly.categories ? { works_broadly_categories: true } : {}),
            ...(v2WorksBroadly.suppliers ? { works_broadly_suppliers: true } : {}),
            ...(v2WorksBroadly.accounts ? { works_broadly_accounts: true } : {}),
          };
      const payload = {
        role: v2SelectedRole,
        focus_areas: focusAreas,
      };
      if (v2SelectedRole === 'other' && v2RoleDescription.trim()) {
        payload.role_description = v2RoleDescription.trim();
      }
      const data = await apiFetch(`${v2Prefix}/session/start`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!mountedRef.current) return;
      setSessionId(data.session_id);
      setV2Phase(data.phase);
      setRoundNumber(data.round_number || 1);
      setQuestions(data.questions || []);
      setCompletedSteps([1, 2]);
      setCurrentStep(3);
    } catch (err) {
      if (mountedRef.current) setV2Error(err.message || 'Could not start interview session.');
    } finally {
      if (mountedRef.current) setV2StartLoading(false);
    }
  }, [apiFetch, v2SelectedRole, v2StepSelections, v2SkipSelection, v2RoleDescription, v2WorksBroadly]);

  /* ─── v2: Trigger Question Generation ────────────────────────── */
  const triggerV2QuestionGen = useCallback(async (sid) => {
    const targetSession = sid || sessionId;
    if (!targetSession) return;
    setV2QuestionGenLoading(true);

    setV2Error(null);
    try {
      const data = await apiFetch(`${v2Prefix}/generate-questions`, {
        method: 'POST',
        body: JSON.stringify({ session_id: targetSession }),
      });
      if (!mountedRef.current) return;

      if (data.status === 'skipped_to_review') {
        setV2Phase('review');
        if (data.gaps_closed) setGapsClosed(data.gaps_closed);
        // Fetch pending field writes
        const statusData = await apiFetch(`${v2Prefix}/session/status?session_id=${targetSession}`);
        if (statusData?.pending_field_writes) {
          setFieldWrites(statusData.pending_field_writes.map(fw => ({ ...fw })));
        }
        return;
      }

      // Phase 5 review: no questions, show confirmation screen
      if (data.status === 'phase6_review' || data.phase === 'phase6_review') {
        setV2Phase('phase6_review');
        setSynthesisStatusRaw(data.synthesis_status || null);
        if (data.phase6_synthesis) {
          setV2PhaseSummaries(prev => ({ ...prev, phase6_synthesis: data.phase6_synthesis }));
        }
        if (data.phase_labels) setV2PhaseLabels(data.phase_labels);
        return;
      }

      setV2Phase(data.phase);
      setRoundNumber(data.round_number || 1);
      setQuestions(data.questions || []);
      setFollowUpIdx(0);
      setFollowUpAnswer('');
      if (data.welcome_message) {
        setV2WelcomeMessage(data.welcome_message);
      }
      if (data.phase_labels) setV2PhaseLabels(data.phase_labels);
    } catch (err) {
      if (mountedRef.current) setV2Error(err.message || 'Failed to generate questions.');
    } finally {
      if (mountedRef.current) setV2QuestionGenLoading(false);
    }
  }, [apiFetch, sessionId]);

  /* ─── v2: Auto-trigger question gen when phase is 'profiled' ─── */
  useEffect(() => {
    if (!sessionId) return;
    // Trigger generation for phases that need it
    if (v2Phase === 'profiled' || v2Phase === 'phase6_confirmed' ||
        (v2Phase && v2Phase.endsWith('_synthesizing'))) {
      triggerV2QuestionGen(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Phase, sessionId]);

  /* ─── v2: 60-second timeout on question generation ────────────── */
  useEffect(() => {
    if (!v2QuestionGenLoading) {
      return;
    }
    const timer = setTimeout(() => {
      if (mountedRef.current && v2QuestionGenLoading) {
        setV2QuestionGenLoading(false);
        setV2Error('Question generation timed out. The AI may be busy — you can try again.');
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [v2QuestionGenLoading]);

  /* ─── v2: Submit all answers and trigger synthesis ────────────── */
  const handleV2SubmitAnswers = useCallback(async () => {
    if (!sessionId) return;
    setSynthesisSubmitting(true);
    setSynthesisSubmitError(null);
    try {
      // Answers are already saved individually via /session/answer as the user
      // progresses through each question — just trigger synthesis.
      //
      const data = await apiFetch(`${v2Prefix}/session/synthesise`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!mountedRef.current) return;

      if (data.phase === 'review') {
        setV2Phase('review');
        if (data.gaps_closed) setGapsClosed(data.gaps_closed);
        if (data.pending_field_writes) {
          setFieldWrites(data.pending_field_writes.map(fw => ({ ...fw })));
        }
      } else {
        // Synthesis done, phase is now *_synthesizing → trigger question gen
        setV2Phase(data.phase);
      }
    } catch (err) {
      if (mountedRef.current) setSynthesisSubmitError(err.message || 'Synthesis failed.');
    } finally {
      if (mountedRef.current) setSynthesisSubmitting(false);
    }
  }, [apiFetch, sessionId, questions]);

  /* ─── v2 Render: Screen 1 — Role Selection ───────────────────── */
  const renderV2RoleSelection = () => {
    if (v2RolesLoading) {
      return (
        <div className="interview-loading">
          <LoadingSpinner size="large" />
          <p className="interview-loading-text">Loading roles...</p>
        </div>
      );
    }

    return (
      <Card>
        <h2 className="interview-section-title">What is your role?</h2>
        <p className="interview-section-desc">
          Select the role that best describes how you interact with procurement.
          This helps us ask the right questions.
        </p>
        {v2Error && <ErrorBanner message={v2Error} />}
        <div className="interview-role-grid">
          {v2Roles.map(role => (
            <button
              key={role.id}
              className={`interview-role-card ${v2SelectedRole === role.id ? 'interview-role-card--selected' : ''}`}
              onClick={() => handleV2RoleSelect(role.id)}
            >
              <span className="interview-role-label">{role.label}</span>
              {v2SelectedRole === role.id && (
                <span className="interview-role-check">{Icons.check}</span>
              )}
            </button>
          ))}
        </div>
        {v2SelectedRole === 'other' && (
          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '6px' }}>
              Briefly describe your role
            </label>
            <input
              type="text"
              placeholder="e.g., 'I manage our ERP system and procurement data'"
              value={v2RoleDescription}
              onChange={(e) => setV2RoleDescription(e.target.value.slice(0, 200))}
              maxLength={200}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #E2E5EA',
                borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
              }}
            />
            <p style={{ fontSize: '11px', color: '#8A94A6', margin: '4px 0 0 0' }}>
              {v2RoleDescription.length}/200 characters
            </p>
          </div>
        )}
        <div className="interview-step-actions">
          {!portalMode && (
            <Button variant="secondary" onClick={() => navigate('/home')} icon={Icons.arrowLeft}>
              Back to Dashboard
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => {
              if (v2SelectedRole) {
                // Leadership skips Screen 2 — go straight to session start
                if (v2SkipSelection) {
                  setCompletedSteps([1, 2]);
                  setCurrentStep(3);
                  handleV2StartSession();
                } else {
                  setCurrentStep(2);
                  setCompletedSteps([1]);
                  // useEffect on v2CurrentStep + currentStep handles loading items
                }
              }
            }}
            disabled={!v2SelectedRole || v2EntitiesLoading || (v2SelectedRole === 'other' && !v2RoleDescription.trim())}
            icon={v2EntitiesLoading ? null : Icons.arrowRight}
          >
            {v2EntitiesLoading ? 'Loading...' : 'Continue'}
          </Button>
        </div>
      </Card>
    );
  };

  /* ─── v2 Render: Screen 2 — Multi-Step Entity Selection ───────── */
  const renderV2EntitySelection = () => {
    if (v2EntitiesLoading && v2StepItems.length === 0) {
      return (
        <div className="interview-loading">
          <LoadingSpinner size="large" />
          <p className="interview-loading-text">Loading your options</p>
        </div>
      );
    }

    const steps = v2EntitySteps;
    if (!steps || steps.length === 0) {
      return (
        <Card>
          <p className="interview-section-desc" style={{ color: '#6B7280' }}>
            No entity selection needed for this role.
          </p>
          <div className="interview-step-actions">
            <Button variant="secondary" onClick={() => { setCurrentStep(1); setCompletedSteps([]); }} icon={Icons.arrowLeft}>
              Back
            </Button>
            <Button variant="primary" onClick={handleV2StartSession} icon={Icons.arrowRight}>
              Continue
            </Button>
          </div>
        </Card>
      );
    }

    const step = steps[v2CurrentStep];
    if (!step) {
      // All steps done — start session
      handleV2StartSession();
      return <div className="interview-loading"><LoadingSpinner size="large" /><p>Starting interview...</p></div>;
    }

    const etype = step.entity_type;
    const key = etype === 'category' ? 'categories' : etype === 'account' ? 'accounts' : 'suppliers';
    const selections = v2StepSelections[key] || [];
    const minSel = step.min_select || 0;
    const maxSel = step.max_select || 10;
    const isRequired = step.required;

    // Items to display: inline (categories) or paginated (suppliers/accounts)
    const items = etype === 'category' ? (step.items || []) : v2StepItems;
    const totalCount = etype === 'category' ? items.length : v2StepTotalCount;
    const showSearch = etype !== 'category';
    const pageSize = 20;
    const totalPages = Math.ceil(totalCount / pageSize);
    const showPagination = etype !== 'category' && totalPages > 1;

    const isBroadly = v2WorksBroadly[key];
    const canProceed = isBroadly || !isRequired || selections.length >= Math.max(minSel, 1);
    const isLastStep = v2CurrentStep >= steps.length - 1;

    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Step {v2CurrentStep + 1} of {steps.length}
          </span>
        </div>
        <h2 className="interview-section-title">{step.prompt_text || 'Select items'}</h2>
        <p className="interview-section-desc">
          {isRequired
            ? `Select at least ${Math.max(minSel, 1)} item${Math.max(minSel, 1) > 1 ? 's' : ''} to continue.`
            : 'This step is optional. You can skip if this doesn\'t apply to your work.'}
        </p>
        <p style={{ fontSize: '12px', color: '#8A94A6', margin: '0 0 12px 0' }}>
          Focus on the ones you work with most or have the strongest opinions about
        </p>

        {/* "I work broadly" toggle — visually distinct from skip options */}
        <button
          onClick={() => {
            const newVal = !v2WorksBroadly[key];
            setV2WorksBroadly(prev => ({ ...prev, [key]: newVal }));
            if (newVal) {
              // Clear any selections when toggling on
              setV2StepSelections(prev => ({ ...prev, [key]: [] }));
            }
          }}
          style={{
            width: '100%',
            padding: '10px 16px',
            marginBottom: '12px',
            background: isBroadly ? '#2251FF' : '#F0F7FF',
            color: isBroadly ? '#FFFFFF' : '#2251FF',
            border: `1px solid ${isBroadly ? '#2251FF' : '#B8D4FF'}`,
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.15s ease',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 8h12M8 2v12" stroke={isBroadly ? '#FFFFFF' : '#2251FF'} strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {isBroadly ? 'Working broadly across most of these' : 'I work broadly across most of these'}
        </button>

        {v2Error && <ErrorBanner message={v2Error} />}

        {/* Entity selection area — dimmed when "works broadly" is active */}
        <div style={{
          opacity: isBroadly ? 0.35 : 1,
          pointerEvents: isBroadly ? 'none' : 'auto',
          transition: 'opacity 0.15s ease',
        }}>

        {/* Search box for paginated lists */}
        {showSearch && (
          <div style={{ marginBottom: '12px' }}>
            <input
              type="text"
              placeholder={`Search ${etype}s...`}
              value={v2StepSearch}
              onChange={(e) => handleStepSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #E2E5EA',
                borderRadius: '6px', fontSize: '14px', outline: 'none',
              }}
            />
          </div>
        )}

        {/* Selections panel */}
        {selections.length > 0 && (
          <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#F0F7FF', borderRadius: '6px', border: '1px solid #D0E0F0' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#2251FF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Your selections ({selections.length})
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {selections.map((s, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 8px', background: '#fff', borderRadius: '4px',
                  fontSize: '12px', color: '#111827', border: '1px solid #D0E0F0',
                }}>
                  {s.name}
                  <button
                    onClick={() => handleV2EntityToggle(etype, s)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', color: '#6B7280' }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Helper text for category-filtered supplier lists */}
        {etype === 'supplier' && v2HasCategoryFilter && !v2StepSearch && (
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px 0' }}>
            Suppliers in your selected categories are shown first. Use the search bar to find others.
          </p>
        )}

        {/* Entity list */}
        {v2EntitiesLoading && items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px' }}><LoadingSpinner size="small" /></div>
        ) : (
          <div className="interview-focus-grid">
            {(() => {
              // For supplier lists with category filter, show section labels
              const hasCatFilter = etype === 'supplier' && v2HasCategoryFilter && !v2StepSearch;
              let shownOtherLabel = false;
              return items.map((item) => {
                const itemName = item.name || item.entity_name || '';
                const itemId = item.id || null;
                const isSelected = selections.some(s =>
                  (s.id && s.id === itemId) || (s.name && s.name === itemName)
                );
                const isDisabled = !isSelected && selections.length >= maxSel;
                const inCat = item.in_selected_categories;

                // Section divider between category-matched and other suppliers
                let sectionLabel = null;
                if (hasCatFilter && !inCat && !shownOtherLabel) {
                  shownOtherLabel = true;
                  sectionLabel = (
                    <div key="other-suppliers-label" style={{
                      gridColumn: '1 / -1', padding: '8px 0 4px',
                      fontSize: '11px', fontWeight: 600, color: '#6B7280',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      borderTop: '1px solid #E2E5EA', marginTop: '4px',
                    }}>
                      Other company suppliers
                    </div>
                  );
                }

                const card = (
                  <button
                    key={`${etype}-${itemId || itemName}`}
                    className={`interview-focus-card ${isSelected ? 'interview-focus-card--selected' : ''} ${isDisabled ? 'interview-focus-card--disabled' : ''}`}
                    onClick={() => !isDisabled && handleV2EntityToggle(etype, { id: itemId, name: itemName })}
                    disabled={isDisabled}
                  >
                    <span className="interview-focus-label">{itemName}</span>
                    {isSelected && <span className="interview-focus-check">{Icons.check}</span>}
                  </button>
                );

                return sectionLabel ? [sectionLabel, card] : card;
              });
            })()}
          </div>
        )}

        {/* Pagination controls */}
        {showPagination && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px', padding: '8px 0' }}>
            <button
              disabled={v2StepPage <= 1}
              onClick={() => {
                const p = v2StepPage - 1;
                setV2StepPage(p);
                loadStepItems(v2CurrentStep, v2StepSearch, p);
              }}
              style={{
                padding: '6px 14px', fontSize: '13px', borderRadius: '6px',
                border: '1px solid #E2E5EA', background: v2StepPage <= 1 ? '#F8F9FB' : '#fff',
                color: v2StepPage <= 1 ? '#C0C4CC' : '#111827', cursor: v2StepPage <= 1 ? 'default' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>
              Page {v2StepPage} of {totalPages}
            </span>
            <button
              disabled={v2StepPage >= totalPages}
              onClick={() => {
                const p = v2StepPage + 1;
                setV2StepPage(p);
                loadStepItems(v2CurrentStep, v2StepSearch, p);
              }}
              style={{
                padding: '6px 14px', fontSize: '13px', borderRadius: '6px',
                border: '1px solid #E2E5EA', background: v2StepPage >= totalPages ? '#F8F9FB' : '#fff',
                color: v2StepPage >= totalPages ? '#C0C4CC' : '#111827', cursor: v2StepPage >= totalPages ? 'default' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        )}
        {/* Show total count for non-category lists */}
        {etype !== 'category' && totalCount > 0 && (
          <p style={{ fontSize: '12px', color: '#8A94A6', textAlign: 'center', margin: '4px 0 0' }}>
            {totalCount} suppliers total
          </p>
        )}

        </div>{/* end entity selection area (dimmed when works broadly) */}

        <p className="interview-focus-hint">
          {isBroadly ? 'Working broadly — no specific selections needed' : `${selections.length} of ${minSel}-${maxSel} selected`}
        </p>

        <div className="interview-step-actions">
          <Button
            variant="secondary"
            onClick={() => {
              if (v2CurrentStep > 0) {
                // Go back to previous step — clear downstream selections
                // useEffect on v2CurrentStep handles loading items + resetting search/page
                setV2StepSelections(prev => ({ ...prev, [key]: [] }));
                setV2CurrentStep(v2CurrentStep - 1);
              } else {
                setCurrentStep(1);
                setCompletedSteps([]);
              }
            }}
            icon={Icons.arrowLeft}
          >
            Back
          </Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Skip button for optional steps */}
            {!isRequired && step.skip_label && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (isLastStep) {
                    handleV2StartSession();
                  } else {
                    // useEffect on v2CurrentStep handles loading items
                    setV2CurrentStep(v2CurrentStep + 1);
                  }
                }}
              >
                {step.skip_label}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                if (isLastStep) {
                  handleV2StartSession();
                } else {
                  // useEffect on v2CurrentStep handles loading items
                  setV2CurrentStep(v2CurrentStep + 1);
                }
              }}
              disabled={!canProceed || v2StartLoading}
              icon={v2StartLoading ? null : Icons.arrowRight}
            >
              {v2StartLoading ? 'Starting...' : isLastStep ? 'Begin Interview' : 'Continue'}
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  /* ─── v2 Render: Step 3 — Phase-based interview ────────────── */
  /* ─── v2: Confirm Phase 5 synthesis ──────────────────────────── */
  const handleConfirmSynthesis = useCallback(async () => {
    if (!sessionId || v2ConfirmSynthLoading) return;
    setV2ConfirmSynthLoading(true);
    setV2Error(null);
    try {
      const data = await apiFetch(`${v2Prefix}/session/${sessionId}/confirm-synthesis`, {
        method: 'POST',
      });
      if (!mountedRef.current) return;
      if (data.phase) {
        setV2Phase(data.phase);  // phase6_confirmed → triggers auto-gen for phase7
      }
    } catch (err) {
      if (mountedRef.current) setV2Error(err.message || 'Failed to confirm synthesis.');
    } finally {
      if (mountedRef.current) setV2ConfirmSynthLoading(false);
    }
  }, [apiFetch, sessionId, v2ConfirmSynthLoading]);

  /* ─── v2: Get current phase label + number for progress indicator ── */
  const getPhaseProgress = () => {
    if (!v2Phase) return null;
    // Extract phase number from phase name (e.g., "phase2_active" → 2)
    const match = v2Phase.match(/phase(\d)/);
    if (!match) return null;
    const phaseNum = parseInt(match[1], 10);
    const phaseKey = `phase${phaseNum}`;
    const label = v2PhaseLabels[phaseKey] || PHASE_LABELS_DEFAULT[phaseKey] || '';
    return { phaseNum, label, total: 8 };
  };

  const renderV2Step3 = () => {
    if (interviewComplete) {
      return renderDone();
    }

    // Question generation in progress
    if (v2QuestionGenLoading) {
      return (
        <div className="interview-loading">
          <LoadingSpinner size="large" />
          <p className="interview-loading-text">Preparing your questions...</p>
        </div>
      );
    }

    // Error state
    if (v2Error && !questions.length && v2Phase !== 'phase6_review') {
      return (
        <div className="interview-empty-state">
          {Icons.warning}
          <p className="interview-empty-state-text" style={{ marginBottom: '4px' }}>Something went wrong generating your questions.</p>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>{v2Error}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button variant="primary" onClick={() => triggerV2QuestionGen(sessionId)} icon={Icons.retry}>
              Try Again
            </Button>
            <Button variant="secondary" onClick={abandonV2Session}>
              Start Over
            </Button>
          </div>
        </div>
      );
    }

    // Phase 5 confirmation screen
    if (v2Phase === 'phase6_review') {
      const synthesis = v2PhaseSummaries.phase6_synthesis || '';
      // Item 1.3b: a failed AI synthesis call leaves an empty phase6_synthesis
      // string. Without this check the fallback would render "No summary available"
      // — indistinguishable from a healthy session that genuinely produced nothing.
      const synthesisFailed =
        normalizeSynthesisStatus(synthesisStatusRaw) === SYNTHESIS_STATUS.FAILED;
      return (
        <div>
          <div className="interview-phase-progress">
            <div className="interview-phase-progress-bar">
              <div className="interview-phase-progress-fill" style={{ width: `${(7 / 8) * 100}%` }} />
            </div>
            <span className="interview-phase-progress-label">Phase 6 of 8: Synthesis & Confirmation</span>
          </div>
          <Card>
            <div className="interview-step-header">
              <h2 className="interview-step-title">Review interview summary</h2>
              <p className="interview-step-subtitle">
                Below is a summary of everything you shared across the interview. Review it for accuracy before we save it as intelligence.
              </p>
            </div>
            <div className="interview-phase5-summary">
              {synthesisFailed ? (
                <p
                  className="interview-phase5-paragraph"
                  style={{ color: '#DC2626', backgroundColor: '#DC262610', borderLeft: '3px solid #DC2626', padding: '10px 14px', borderRadius: '4px' }}
                >
                  {SYNTHESIS_COPY.FAILED_INTERVIEWEE}
                </p>
              ) : synthesis ? (
                synthesis.split('\n').map((paragraph, i) => (
                  <p key={i} className="interview-phase5-paragraph">{paragraph}</p>
                ))
              ) : (
                <p className="interview-phase5-paragraph" style={{ color: 'var(--color-text-secondary)' }}>
                  {SYNTHESIS_COPY.COMPLETE_EMPTY_FALLBACK}
                </p>
              )}
            </div>
            {v2Error && (
              <div style={{ color: '#DC2626', fontSize: '13px', marginBottom: '12px' }}>{v2Error}</div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button
                variant="primary"
                onClick={handleConfirmSynthesis}
                disabled={v2ConfirmSynthLoading}
              >
                {v2ConfirmSynthLoading ? 'Saving...' : 'Confirm & Continue'}
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    // Review screen (final, after Phase 6)
    if (v2Phase === 'review' || fieldWrites.length > 0) {
      return renderReview();
    }

    // Synthesis in progress
    if (synthesisSubmitting) {
      return (
        <div className="interview-loading">
          <LoadingSpinner size="large" />
          <p className="interview-loading-text">Analyzing your answers...</p>
        </div>
      );
    }

    // Phase progress indicator
    const progress = getPhaseProgress();

    // Active phase with questions — use follow-up renderer
    if (questions.length > 0) {
      return (
        <div>
          {progress && (
            <div className="interview-phase-progress">
              <div className="interview-phase-progress-bar">
                <div className="interview-phase-progress-fill" style={{ width: `${((progress.phaseNum + 1) / progress.total) * 100}%` }} />
              </div>
              <span className="interview-phase-progress-label">
                Phase {progress.phaseNum + 1} of {progress.total}: {progress.label}
              </span>
            </div>
          )}
          {v2WelcomeMessage && followUpIdx === 0 && (
            <div style={{
              padding: '14px 18px',
              backgroundColor: '#EEF2FF',
              borderRadius: '8px',
              border: '1px solid #C7D2FE',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#3730A3',
              lineHeight: '1.5',
            }}>
              {v2WelcomeMessage}
            </div>
          )}
          {renderFollowUpRound()}
        </div>
      );
    }

    // Waiting for questions (phase just advanced)
    return (
      <div className="interview-loading">
        <LoadingSpinner size="large" />
        <p className="interview-loading-text">Loading questions...</p>
      </div>
    );
  };

  /* ─── Main Render ──────────────────────────────────────────── */

  return (
    <div className="interview-page">
      <div className="interview-header">
        <h1 className="interview-title">Knowledge Interview</h1>
        <StepIndicator currentStep={currentStep} completedSteps={completedSteps} steps={STEPS} />
      </div>

      {isDemoMode && (
        <div className="interview-demo-banner">
          Demo Mode — answers will be auto-generated for QA testing
        </div>
      )}

      <div className="interview-body">
        {currentStep === 1 && renderV2RoleSelection()}
        {currentStep === 2 && renderV2EntitySelection()}
        {currentStep === 3 && renderV2Step3()}
      </div>

      {/* Cancel Interview — visible during active interview (step 3, not complete) */}
      {currentStep === 3 && !interviewComplete && sessionId && (
        <div style={{ textAlign: 'center', marginTop: '20px', paddingBottom: '16px' }}>
          <button
            onClick={() => setShowAbandonConfirm(true)}
            style={{
              background: 'none', border: '1px solid #D1D5DB', borderRadius: '6px',
              color: 'var(--color-text-secondary, #6B7280)',
              fontSize: '13px', cursor: 'pointer',
              padding: '6px 16px',
            }}
          >
            Cancel Interview
          </button>
        </div>
      )}

      {/* Abandon confirmation dialog */}
      {showAbandonConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px',
            maxWidth: '400px', width: '90%', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 600 }}>Cancel Interview?</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: 'var(--color-text-secondary, #6B7280)', lineHeight: '1.5' }}>
              Your answers from this session will not be saved.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAbandonConfirm(false)}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #D1D5DB',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px',
                }}
              >
                Keep Going
              </button>
              <button
                onClick={() => { setShowAbandonConfirm(false); abandonV2Session(); }}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  backgroundColor: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: '13px',
                }}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Export with Error Boundary ──────────────────────────────── */
function InterviewWizard({ portalMode, portalToken, onComplete, onAuthExpired }) {
  return (
    <InterviewErrorBoundary>
      <InterviewWizardInner
        portalMode={portalMode}
        portalToken={portalToken}
        onComplete={onComplete}
        onAuthExpired={onAuthExpired}
      />
    </InterviewErrorBoundary>
  );
}

export default InterviewWizard;
